<script setup lang="ts">
import type { ButtonProps } from 'naive-ui'
import type { Project } from '~/types/project.type'
import type { Task } from '~/types/task.type'
import { TimeEntryModal, UIcon } from '#components'
import { tableActionsIsInsideDropdownInjectionKey } from '~/injections'

const props = defineProps({
  taskId: {
    type: Number as PropType<Task['id']>,
    required: true,
  },
  projectId: {
    type: Number as PropType<Project['id']>,
    required: true,
  },
  buttonProps: {
    type: Object as PropType<ButtonProps>,
    default: () => ({}),
  },
})

const overlay = useOverlay()
const loadingBar = useNaiveLoadingBar()
const timeEntryModalLoading = ref(false)

const { data: projectsData, execute: fetchProjects } = useApiProjectGetListUnpaginated({
  withLeads: true,
  withClient: true,
  isActive: true,
  additionalProjects: [props.projectId],
}, { immediate: false, watch: false })

const { data: taskListsData, refresh: fetchTaskLists } = useApiTaskListGetList({
  projectId: props.projectId,
  withTasks: true,
  withCompleted: false,
  additionalTasks: [props.taskId],
}, { immediate: false, watch: false })

const { data: usersData, execute: fetchUsers } = useApiUserGetList({}, { immediate: false, watch: false })

const timeEntryModal = overlay.create(TimeEntryModal)

async function openTimeEntryModal() {
  timeEntryModalLoading.value = true
  loadingBar.start()
  await Promise.all([fetchProjects(), fetchTaskLists(), fetchUsers()])
  loadingBar.finish()
  timeEntryModalLoading.value = false
  timeEntryModal.open({
    title: 'Zeiteintrag erstellen',
    projectId: props.projectId,
    taskId: props.taskId,
    projects: projectsData.value?.data,
    taskLists: taskListsData.value?.data,
    users: usersData.value?.data,
  })
}

const isInsideDropdown = inject(tableActionsIsInsideDropdownInjectionKey, false)
</script>

<template>
  <NTooltip :disabled="isInsideDropdown">
    <template #trigger>
      <NButton
        :loading="timeEntryModalLoading"
        :quaternary="isInsideDropdown"
        :class="{ '!justify-start': isInsideDropdown }"
        v-bind="buttonProps"
        @click.stop="openTimeEntryModal"
      >
        <template #icon>
          <UIcon name="i-ic-baseline-more-time" />
        </template>
        <span v-if="isInsideDropdown">Neuer Zeiteintrag für Aufgabe</span>
      </NButton>
    </template>
    Zeiteintrag für diese Aufgabe hinzufügen
  </NTooltip>
</template>
